/**
 * Provider Registry voor Review System
 * 
 * Dit bestand beheert de registratie van verschillende review providers.
 * Elke provider moet dezelfde interface implementeren voor naadloze uitwisselbaarheid.
 * 
 * @typedef {Object} ProviderConfig
 * @property {string} name - Provider naam (bijv. 'yotpo', 'judgeme', 'loox')
 * @property {string} module - Pad naar de provider module
 * @property {string} [description] - Beschrijving van de provider
 */

/**
 * Registry van beschikbare review providers
 * @type {Map<string, ProviderConfig>}
 */
const providerRegistry = new Map();

// Registreer providers
providerRegistry.set('yotpo', {
  name: 'yotpo',
  module: 'review-provider-yotpo',
  description: 'Yotpo Reviews - Storefront API'
});

// Toekomstige providers kunnen hier worden toegevoegd:
// providerRegistry.set('judgeme', {
//   name: 'judgeme',
//   module: 'review-provider-judgeme',
//   description: 'Judge.me Reviews'
// });
// providerRegistry.set('loox', {
//   name: 'loox',
//   module: 'review-provider-loox',
//   description: 'Loox Photo Reviews'
// });

/**
 * Haalt een provider adapter op basis van de provider naam
 * @param {string} providerName - Naam van de provider ('yotpo', 'judgeme', etc.)
 * @returns {Promise<Object>} Provider adapter met fetchSummary en fetchReviews methods
 * @throws {Error} Als provider niet gevonden wordt
 */
export async function getProvider(providerName) {
  if (!providerName) {
    throw new Error('Provider naam is vereist');
  }

  const providerConfig = providerRegistry.get(providerName);
  
  if (!providerConfig) {
    const availableProviders = Array.from(providerRegistry.keys()).join(', ');
    throw new Error(`Provider '${providerName}' niet gevonden. Beschikbare providers: ${availableProviders}`);
  }

  try {
    // In Shopify moeten we de module naam gebruiken zonder pad
    const providerModule = await import(`./${providerConfig.module}.js`);
    
    // Valideer dat de provider de vereiste interface implementeert
    if (typeof providerModule.fetchSummary !== 'function') {
      throw new Error(`Provider '${providerName}' implementeert geen fetchSummary functie`);
    }
    
    if (typeof providerModule.fetchReviews !== 'function') {
      throw new Error(`Provider '${providerName}' implementeert geen fetchReviews functie`);
    }

    return providerModule;
  } catch (error) {
    if (error.code === 'MODULE_NOT_FOUND') {
      throw new Error(`Provider module '${providerConfig.module}.js' niet gevonden voor provider '${providerName}'`);
    }
    throw error;
  }
}

/**
 * Geeft een lijst van alle beschikbare providers
 * @returns {Array<ProviderConfig>} Lijst van beschikbare providers
 */
export function getAvailableProviders() {
  return Array.from(providerRegistry.values());
}

/**
 * Controleert of een provider beschikbaar is
 * @param {string} providerName - Naam van de provider
 * @returns {boolean} True als provider beschikbaar is
 */
export function isProviderAvailable(providerName) {
  return providerRegistry.has(providerName);
}

// Export naar globale scope voor gebruik in inline scripts
window.getProvider = getProvider;
window.getAvailableProviders = getAvailableProviders;
window.isProviderAvailable = isProviderAvailable;
